// How much of this step is left, as a ring rather than a sentence.
//
// "7 fields left" is accurate and tells you nothing at a glance — you have to
// read it, and it looks the same at 7 as at 1. A ring is read in peripheral
// vision: the gap tells you how much is outstanding before you have parsed any
// text, and it fills as you type, which is its own quiet encouragement.
//
// It is also the scroll affordance. The whole control is a button: pressing it
// takes you to the next field still needed, which on a long step is the thing
// the customer actually wants and previously had to hunt for.
import React from "react";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const GOLD = "#C9A777";
const INK = "#3d322b";
const MUTED = "#7B6E66";

const SIZE = 38;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * @param {Object} props
 * @param {{done: number, total: number}} props.progress
 * @param {boolean} [props.canJump]     Whether there is a field to scroll to.
 * @param {() => void} [props.onJump]
 * @param {boolean} [props.nudge]       Animate the chevron (the bar is docked).
 */
const StepProgress = ({ progress, canJump, onJump, nudge }) => {
  const total = Math.max(progress.total, 0);
  const done = Math.min(progress.done, total);
  const remaining = Math.max(total - done, 0);
  const complete = remaining === 0;

  // A step with nothing required has nothing to report.
  if (total === 0) return null;

  const fraction = total ? done / total : 1;

  // The caption never contains a digit: the ring already shows the count, and
  // repeating it read as "4 — 4 of 5 to go". Instead the ring *is* the number
  // in the phrase, so the control reads left-to-right as "[4] left to fill in".
  const caption = complete ? "Ready to continue" : "left to fill in";

  const control = (
    <Stack
      component={canJump ? "button" : "div"}
      type={canJump ? "button" : undefined}
      onClick={canJump ? onJump : undefined}
      direction="row"
      alignItems="center"
      spacing={1.25}
      aria-label={
        complete ? "All required fields complete" : `${remaining} fields left — go to the next one`
      }
      sx={{
        border: 0,
        p: 0,
        bgcolor: "transparent",
        fontFamily: "inherit",
        textAlign: "left",
        cursor: canJump ? "pointer" : "default",
        borderRadius: 999,
        transition: "opacity .2s ease",
        "&:hover": canJump ? { "& .step-progress-caption": { color: INK } } : {},
        "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: 4 },
      }}
    >
      <Box sx={{ position: "relative", width: SIZE, height: SIZE, flexShrink: 0 }}>
        <Box
          component="svg"
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden
          // Start the arc at 12 o'clock rather than 3.
          sx={{ transform: "rotate(-90deg)", display: "block" }}
        >
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(201,167,119,0.22)"
            strokeWidth={STROKE}
          />
          <Box
            component="circle"
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={complete ? "#5F9E6E" : GOLD}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            // The gap *is* the remaining work — no number needed to read it.
            strokeDashoffset={CIRCUMFERENCE * (1 - fraction)}
            sx={{
              transition:
                "stroke-dashoffset .45s cubic-bezier(.2,.8,.2,1), stroke .3s ease",
            }}
          />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {complete ? (
            <CheckRoundedIcon sx={{ fontSize: 18, color: "#5F9E6E" }} />
          ) : (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 700,
                color: INK,
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {remaining}
            </Typography>
          )}
        </Box>
      </Box>

      <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
        <Typography
          className="step-progress-caption"
          sx={{
            color: MUTED,
            fontSize: "0.875rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
            transition: "color .2s ease",
          }}
        >
          {caption}
        </Typography>
        {canJump && (
          <KeyboardArrowDownRoundedIcon
            sx={{
              fontSize: 18,
              color: MUTED,
              animation: nudge ? "step-progress-nudge 1.8s ease-in-out infinite" : "none",
              "@keyframes step-progress-nudge": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(3px)" },
              },
            }}
          />
        )}
      </Stack>
    </Stack>
  );

  if (!canJump) return control;

  return (
    <Tooltip title="Go to the next field you still need" arrow placement="top">
      {control}
    </Tooltip>
  );
};

export default StepProgress;
